import { mockWorkspaces } from './workspaces';

export type BrandTargetMarket = string;

export interface BrandProfileMock {
  companyName: string;
  websiteUrl: string;
  description: string;
  industry: string;
  brandIdentity: string[];
  productsServices: string[];
  targetMarket: BrandTargetMarket;
  apiReference: {
    getEndpoint: string;
    putEndpoint: string;
    requestExample: string;
    responseExample: string;
  };
}

const workspaceUrl = (workspaceId: string) => {
  const workspace = mockWorkspaces.find((item) => item.id === workspaceId);
  return workspace ? `https://${workspace.domain}` : 'https://tradingbase.ai';
};

export const mockBrandProfileDefault: BrandProfileMock = {
  companyName: 'TradingBase',
  websiteUrl: workspaceUrl('workspace_tradingbase'),
  description: 'TradingBase 帮助品牌洞察 AI 可见度，优化在 AI 搜索结果中的品牌叙事，并提升在主流 AI 发现渠道中的曝光表现。',
  industry: 'profile.industryOptions.fintech',
  brandIdentity: ['数据驱动', '值得信赖', '现代感', '易于操作'],
  productsServices: ['AI 可见度监控', '品牌情报', '提示词优化', '竞品分析报告'],
  targetMarket: 'US',
  apiReference: {
    getEndpoint: 'GET /workspace/:workspaceId/profile',
    putEndpoint: 'PUT /workspace/:workspaceId/profile',
    requestExample: `{
  "company_name": "TradingBase",
  "website_url": "https://tradingbase.ai",
  "description": "TradingBase 帮助品牌洞察 AI 可见度...",
  "industry": "fintech",
  "brand_identity": ["数据驱动", "值得信赖", "现代感"],
  "products_services": ["AI 可见度监控", "提示词优化"],
  "target_market": "US"
}`,
    responseExample: `{
  "workspace_id": "workspace_tradingbase",
  "company_name": "TradingBase",
  "website_url": "https://tradingbase.ai",
  "domain": "tradingbase.ai",
  "favicon_url": "https://www.google.com/s2/favicons?domain=tradingbase.ai&sz=128",
  "description": "TradingBase 帮助品牌洞察 AI 可见度...",
  "industry": "fintech",
  "brand_identity": ["数据驱动", "值得信赖", "现代感"],
  "products_services": ["AI 可见度监控", "提示词优化"],
  "target_market": "US"
}`,
  },
};

export const mockBrandProfileByWorkspace: Record<string, BrandProfileMock> = {
  workspace_tradingbase: mockBrandProfileDefault,
  workspace_finpulse: {
    companyName: 'FinPulse',
    websiteUrl: workspaceUrl('workspace_finpulse'),
    description: 'FinPulse 将实时市场信号转化为简洁清晰的品牌情报，服务于投资者、分析师和金融团队。',
    industry: 'profile.industryOptions.fintech',
    brandIdentity: ['快速响应', '清晰表达', '值得信赖', '全球视野'],
    productsServices: ['市场信号追踪', '金融内容监控', '投资者情报'],
    targetMarket: 'GB',
    apiReference: mockBrandProfileDefault.apiReference,
  },
  workspace_cryptoedge: {
    companyName: 'CryptoEdge',
    websiteUrl: workspaceUrl('workspace_cryptoedge'),
    description: 'CryptoEdge 帮助数字资产品牌在 AI 搜索结果中呈现更清晰的产品叙事、风险定位和平台差异化优势。',
    industry: 'profile.industryOptions.fintech',
    brandIdentity: ['自信直接', '安全优先', '产品导向', '专业可信'],
    productsServices: ['交易所情报', '品牌监控', '市场教育内容', '品类追踪'],
    targetMarket: 'SG',
    apiReference: mockBrandProfileDefault.apiReference,
  },
  workspace_marketlens: {
    companyName: 'MarketLens',
    websiteUrl: workspaceUrl('workspace_marketlens'),
    description: 'MarketLens 将经济数据、市场背景和报告工作流整合为专为研究型买家设计的品牌声音与智能工具。',
    industry: 'profile.industryOptions.saas',
    brandIdentity: ['研究导向', '精准严谨', '高管级表达', '结构清晰'],
    productsServices: ['经济数据 API', '研究仪表板', '报告工作流', '洞察订阅'],
    targetMarket: 'DE',
    apiReference: mockBrandProfileDefault.apiReference,
  },
};

export const getMockBrandProfile = (workspaceId: string): BrandProfileMock => (
  mockBrandProfileByWorkspace[workspaceId] ?? mockBrandProfileDefault
);